import { ExclamationCircleFilled, FileTextOutlined } from '@ant-design/icons';
import { InvoiceDetailCard, InvoicePaymentModal } from '@components';
import {
  useInvoiceByOrderId,
  useInvoiceTotals,
  usePaymentHandlers,
  useRelatedOrders,
} from '@hooks';
import { OrderStatus } from '@models';
import { Order, Vehicle } from '@services';
import { useStore } from '@stores';
import { Utils } from '@utils';
import { Button, Form, Modal, Typography } from 'antd';
import { observer } from 'mobx-react';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface IProps {
  order: Order;
  vehicle: Vehicle;
  iconOnly?: boolean;
}

export const OrderInvoiceCard: React.FC<IProps> = observer(
  ({ order, vehicle, iconOnly }) => {
    const { invoiceStore, vehicleStore, authenticationStore, orderStore, serviceStore } = useStore();
    const { relatedCheckedIds: relatedCheck } = orderStore;
    const navigate = useNavigate();

    const [paymentForm] = Form.useForm();
    const [modalType, setModalType] = useState<'payment' | 'refund' | null>(null);

    const { invoice } = useInvoiceByOrderId(order);
    const relatedOrdersHook = useRelatedOrders(order.id);

    const {
      invoiceTotal = 0,
      creditTotal = 0,
      refundTotal = 0,
      refundPaidTotal = 0,
      refundRequestedTotal = 0,
      paymentTotal = 0,
      dueTotal = 0,
      invoiceSubtotal = 0,
      invoiceVat = 0,
    } = useInvoiceTotals(invoice, invoice?.invoicePayments) || {};

    const {
      handlePaymentCancel,
      handlePartialPaymentInput,
      handlePaymentCreate,
      handlePosPayment,
      handleCashRefundCreate,
      handlePosRefund,
    } = usePaymentHandlers({
      invoice,
      paymentForm,
      invoiceTotal,
      paymentTotal,
      dueTotal,
      setModalType,
    });

    const iAmManager =
      (authenticationStore.currentUser?.role &&
        ['Manager', 'ManagerTechnician'].includes(authenticationStore.currentUser.role)) ||
      false;

    const handleGenerateInvoice = useCallback(async () => {
      if (!order || !vehicle) return;

      const orders = relatedOrdersHook.relatedOrders.filter((x) =>
        relatedCheck.includes(x.id)
      );
      orders.push(order);

      const totalAmount = orders.reduce((acc, curr) => acc + Utils.calculateTotalAmount(curr.serviceOrders), 0);
      const totalVatAmount = orders.reduce((acc, curr) => acc + Utils.calculateTotalVat(curr.serviceOrders), 0);
      const maxMileage = Math.max(
        ...orders.map((o) => Number(o.mileage)).filter((m) => !isNaN(m))
      );

      try {
        const mileageResult = await vehicleStore.updateVehicleMileage({ id: vehicle.id, mileage: maxMileage });
        if (!mileageResult) return;

        const invoiceResult = await invoiceStore.create({
          vat: totalVatAmount,
          amount: totalAmount,
          dueDate: 0,
          vinNumber: vehicle.combinedVin,
          makeModel: vehicle.combinedMake + ' ' + vehicle.combinedModel,
          registrationNumber: order.registrationNumber,
          vin: vehicle.combinedVin,
          customerId: order.customerId,
          orders: orders.map((x) => x.id),
          serviceOrders: orders.map((x) => x.serviceOrders).flat(),
          mileage: maxMileage,
        });

        if (invoiceResult) navigate(`/invoices/${invoiceResult.id}`);
      } catch (error) {
        console.error('Error generating invoice:', error);
      }
    }, [order, vehicle, relatedCheck, relatedOrdersHook]);

    const handleConfirmGenerateInvoice = useCallback(() => {
      const completedRelatedOrders = relatedOrdersHook.relatedOrders.filter(
        (x) => x.status === OrderStatus.Completed
      );
      const unchecked = completedRelatedOrders.filter((x) => !relatedCheck.includes(x.id));

      if (completedRelatedOrders.length === 0 || unchecked.length === 0) {
        handleGenerateInvoice();
        return;
      }

      Modal.confirm({
        width: 480,
        title: 'Some related jobs are completed but not selected',
        icon: <ExclamationCircleFilled />,
        okText: 'Continue',
        cancelText: 'Cancel',
        content:
          'You have related items that are completed but not checked. Are you sure you want to continue?',
        onOk: handleGenerateInvoice,
      });
    }, [relatedOrdersHook.relatedOrders, relatedCheck, handleGenerateInvoice]);

    const canGenerate = iAmManager && order.status === OrderStatus.Completed;

    // Before the job is invoiced the card previews what the invoice will say - the live totals
    // of the services on screen - and why it cannot be raised yet, instead of a blank placeholder.
    // Only for statuses where no invoice is fetched; Ready/Paid keep the real card and its loading.
    const awaitingInvoice = order.status !== OrderStatus.Paid && order.status !== OrderStatus.Ready;
    const { labourPrice, partsPrice, vatAmount, totalPrice } = serviceStore.pricingSummary;
    const reason =
      order.status === OrderStatus.Completed
        ? iAmManager
          ? 'Job complete - the invoice can be raised.'
          : 'Job complete - a manager can raise the invoice.'
        : order.status === OrderStatus.Approval
          ? 'Waiting for the customer to approve the estimate.'
          : order.status === OrderStatus.Canceled
            ? 'Cancelled jobs are not invoiced.'
            : 'Invoicing opens once the job is completed.';
    const previewRow = (label: string, value: number, strong = false) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0' }}>
        <Typography.Text type={strong ? undefined : 'secondary'} strong={strong}>
          {label}
        </Typography.Text>
        <Typography.Text strong={strong}>{Utils.formatCurrency(value)}</Typography.Text>
      </div>
    );
    const invoicePreview = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FileTextOutlined style={{ fontSize: 16, color: '#8c8c8c' }} />
          <Typography.Text strong>Not invoiced yet</Typography.Text>
        </div>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {reason}
        </Typography.Text>
        <div
          style={{
            background: '#f7fafd',
            border: '1px solid #e8eef5',
            borderRadius: 8,
            padding: '8px 12px',
          }}
        >
          {previewRow('Labour', labourPrice)}
          {previewRow('Parts', partsPrice)}
          {previewRow('VAT', vatAmount)}
          {previewRow('Total', totalPrice, true)}
        </div>
        {canGenerate && (
          <Button
            type="primary"
            block
            onClick={handleConfirmGenerateInvoice}
            disabled={invoiceStore.loading}
            loading={invoiceStore.loading}
          >
            Generate invoice
          </Button>
        )}
      </div>
    );

    return (
      <>
        <InvoiceDetailCard
          invoice={invoice}
          emptyState={awaitingInvoice ? invoicePreview : undefined}
          invoiceSubtotal={invoiceSubtotal}
          invoiceVat={invoiceVat}
          invoiceTotal={invoiceTotal}
          creditTotal={creditTotal}
          refundTotal={refundTotal}
          refundPaidTotal={refundPaidTotal}
          refundRequestedTotal={refundRequestedTotal}
          paymentTotal={paymentTotal}
          dueTotal={dueTotal}
          onPayClick={() => setModalType('payment')}
        >
          {canGenerate && (
            <Button
              type="primary"
              style={{ width: iconOnly ? '100%' : 150 }}
              onClick={handleConfirmGenerateInvoice}
              disabled={invoiceStore.loading}
              loading={invoiceStore.loading}
            >
              Generate invoice
            </Button>
          )}
        </InvoiceDetailCard>

        <InvoicePaymentModal
          paymentForm={paymentForm}
          modalType={modalType}
          invoiceTotal={invoiceTotal}
          totalDue={dueTotal}
          paymentTotal={paymentTotal}
          handlePaymentCreate={handlePaymentCreate}
          handlePosPayment={handlePosPayment}
          handlePaymentCancel={handlePaymentCancel}
          handlePartialPaymentInput={handlePartialPaymentInput}
          handleCashRefundCreate={handleCashRefundCreate}
          handlePosRefund={handlePosRefund}
        />
      </>
    );
  }
);
